/* eslint-disable react/no-array-index-key */

import {Box, Button, FormControl, FormLabel, Grid, Typography} from '@mui/material';
import {AddressFinder} from '@ideal-postcodes/address-finder';
import {PostcodeLookup} from '@ideal-postcodes/postcode-lookup';

import {isPast} from 'date-fns/esm';
import {FormikErrors} from 'formik';
import {useSnackbar} from 'notistack';
import React, {createRef, useCallback, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {capitalize, upperCase} from 'lodash-es';
import styled from 'styled-components';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {AutoFormProps} from '../../components/AutoForm/AutoFormNew';
import {FixedFooter} from '../../components/Layout/Layout';
import {
  LeadFormValues,
  address1Field,
  address2Field,
  townField,
  postcodeField,
  county,
  moveInDateField,
  country,
} from './addAccountFields';
import {useAddAccountContext} from './AddAccountContext';
import {themeColors} from '../../theme/Theme';
import {useAutoForm, useAutoField} from '../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../shared/UPDATES';
import {newUTCDate} from '../../utils/newUTCDate';
import {SaltusAny} from '../../utils/SaltusAny';

export const SingleAddressForm: React.FC<{readOnly?: boolean}> = ({readOnly = false}) => {
  const {setFieldValue} = useAutoForm<LeadFormValues>();
  const [address1, _, address1Helpers] = useAutoField<string | undefined>(
    address1Field.id
  );
  const textInputRef = useRef<any | null>(null);
  const shouldRenderAddressLookup = useRef(true);
  const onAddressChange = useCallback(
    (address: SaltusAny) => {
      setFieldValue(UPDATES._1600, address1Field.id, address.line_1);
      setFieldValue(UPDATES._1601, address2Field.id, address.line_2);
      setFieldValue(
        UPDATES._1602,
        townField.id,
        capitalize((address as any).post_town || '')
      );
      setFieldValue(
        UPDATES._1603,
        postcodeField.id,
        upperCase((address as any).postcode || '')
      );
      setFieldValue(UPDATES._1604, county.id, upperCase((address as any).county || ''));
    },
    [setFieldValue]
  );
  useEffect(() => {
    if (shouldRenderAddressLookup.current === false) return;
    if (readOnly) {
      return;
    }
    shouldRenderAddressLookup.current = false;
    const c = AddressFinder.setup({
      inputField: textInputRef.current,
      queryOptions: {},
      onAddressRetrieved: (address) => {
        onAddressChange(address);
      },
      apiKey: 'ak_lkcio8wg0zu1IOG1Lf9cyJBtBhNBv',
    });
  }, []);
  return (
    <>
      <Box>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PostcodeLookupComponent
              onAddressSelected={(address: SaltusAny) => onAddressChange(address)}
            />
          </Grid>
          {readOnly ? (
            <Grid item>
              <AutoField
                field={{...address1Field, id: `${address1Field.id}`, readOnly}}
              />
            </Grid>
          ) : (
            <Grid item>
              <FormControl fullWidth>
                <FormLabel>
                  <strong>Address line 1:</strong>
                </FormLabel>
                <input
                  style={{
                    height: 40,
                    width: '100%',
                    borderRadius: 4,
                    border: '1px solid grey',
                    flex: 1,
                    padding: 8,
                  }}
                  onChange={(e) =>
                    address1Helpers.setValue(UPDATES._4500, e.target.value)
                  }
                  value={address1.value}
                  ref={textInputRef}
                  id="af_first_line"
                  className="form-control"
                />
              </FormControl>
            </Grid>
          )}
          {/* <Divider /> */}

          <Grid item>
            <AutoField field={{...address2Field, id: `${address2Field.id}`, readOnly}} />
          </Grid>
          {/* <Grid item>
            <AutoField field={{...address3Field, id: `${address3Field.id}`, readOnly}} />
          </Grid> */}
          <Grid item>
            <AutoField field={{...townField, id: `${townField.id}`, readOnly}} />
          </Grid>
          <Grid item>
            <AutoField field={{...postcodeField, id: `${postcodeField.id}`, readOnly}} />
          </Grid>
          <Grid item>
            <AutoField field={{...county, id: `${county.id}`, readOnly}} />
          </Grid>
          <Grid item>
            <AutoField field={{...country, id: `${country.id}`, readOnly}} />
          </Grid>
          {/* <Grid item>
            <AutoField field={{...taxResidency, id: `${taxResidency.id}`, readOnly}} />
          </Grid>
          <Grid item>
            <AutoField field={{...taxDomicile, id: `${taxDomicile.id}`, readOnly}} />
          </Grid> */}
          <Grid item>
            <AutoField
              field={{...moveInDateField, id: `${moveInDateField.id}`, readOnly}}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export const validateAddress = (values: LeadFormValues): FormikErrors<LeadFormValues> => {
  const errors: FormikErrors<LeadFormValues> = {};
  if (!values.line1) {
    errors.line1 = 'Required';
  }
  if (!values.city && !values.county) {
    errors.city = 'At least one of city or county required';
    errors.county = 'At least one of city or county required';
  }
  if (!values.postcode) {
    errors.postcode = 'Required';
  }
  if (!values.country) {
    errors.country = 'Country is required';
  }
  if (!values.moveInDate) {
    // errors.moveInDate = 'Required';
  } else if (isPast(newUTCDate(values.moveInDate))) {
    // all good
  } else {
    errors.moveInDate = 'Must be in the past';
  }
  return errors;
};

export const AddAccountAddressForm: React.FC<AutoFormProps<LeadFormValues>> = () => {
  const navigate = useNavigate();
  const {goToStep} = useAddAccountContext();
  useEffect(() => {
    goToStep('address');
  }, []);
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const {values, setErrors} = useAutoForm<LeadFormValues>();

  return (
    <>
      <Box mt={1} mb={1} padding={3} bgcolor="white">
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} lg={6}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="h6">Address:</Typography>
              </Grid>
              <Grid item>
                <SingleAddressForm />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box mt={2} />
        <FixedFooter>
          <Grid justifyContent="space-between" container>
            <Button
              onClick={() => navigate('../basic')}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Button
              onClick={() => {
                closeSnackbar();
                const newErrors = validateAddress(values);
                setErrors(newErrors);
                if (Object.keys(newErrors).length) {
                  enqueueSnackbar(
                    `Some fields are missing. Please complete before saving.`,
                    {
                      variant: 'error',
                      autoHideDuration: null,
                    }
                  );
                } else {
                  navigate(`/add-account/summary`);
                }
              }}
              variant="contained"
              color="primary"
            >
              Next
            </Button>
          </Grid>
        </FixedFooter>
      </Box>
    </>
  );
};

const PostcodeContainer = styled('div')`
  input,
  button,
  select {
    height: 40px;
    width: 100%;
    border-radius: 4px;
    margin-bottom: 8px;
    border: 1px solid grey;
    flex: 1;
    background: white;
    padding: 8px;
  }
  select {
    margin-top: 20px;
  }
  button {
    background-color: ${themeColors.backgroundHighlight};
    color: white;
    cursor: pointer;
    border: none;
  }
`;
const PostcodeLookupComponent: React.FC<{onAddressSelected: SaltusAny}> = ({
  ...props
}) => {
  const context = createRef<any>();

  useEffect(() => {
    PostcodeLookup.setup({
      apiKey: 'ak_lkcio8wg0zu1IOG1Lf9cyJBtBhNBv',
      context: context.current,
      buttonLabel: 'Find address',
      placeholder: 'Client postcode',
      ...props,
    });
  }, []);

  return <PostcodeContainer ref={context} />;
};
